/**
 * tiktokOrdersStore — the shapes and pure rules behind TiktokOrdersService.
 *
 * Split from the service for the reason igcomOrdersStore.ts was: a test of a
 * JSON packer has no business needing a tenant id.
 *
 * ── WHY A SECOND LIST AND NOT A CHANNEL COLUMN ON IGCOM_Orders ────────────
 * The two channels do not describe the same thing. A Shopify order has a
 * financial status and a fulfilment status and a real customer id; a TikTok
 * order has ONE state machine, no human order number, and no stable buyer
 * identity at all. Sharing a list would mean half the columns are blank
 * depending on the row's channel, and every reader would have to filter on the
 * channel before it could say anything true — which is the same list twice,
 * wearing one name. It is also the argument the screen already makes for
 * keeping Stored and Live apart.
 *
 * ── TWO WAYS THIS STORE IS NARROWER THAN IG COM'S, BOTH DELIBERATE ────────
 *
 * 1. IT STORES NO BUYER NAME. IG COM's stores one, and safely: Shopify's names
 *    reach every IG COM viewer unmasked, so storing them discloses nothing the
 *    screen does not already show. TikTok is the opposite case. Its names are
 *    masked server-side for everyone not on IGCOM_PII_USERS, so the value a
 *    sync would write depends on WHO PRESSED THE BUTTON — a real name from a
 *    PII user, `M••••• I•••••` from anybody else. That is two bugs in one
 *    column: the masking decision undone for whoever syncs with the rights,
 *    and a column that means different things in different rows for everyone
 *    else. Neither is worth a name nothing on the Insights tab reads. So the
 *    name is dropped at the boundary, here, once.
 *
 * 2. A BUYER KEY IS STORED ONLY WHEN IT IS REAL. `normalizeTiktokOrder` falls
 *    back to `order:<id>` where TikTok supplies no `user_id`, which makes each
 *    order its own customer — the honest answer for a live screen that shows
 *    no repeat figure. Stored, that fallback would be a lie with a long life:
 *    a future repeat-buyer count over this list would read every order as a
 *    distinct first-time shopper and report 0%, which is a statement about
 *    TikTok's data wearing the clothes of a statement about our customers. So
 *    a synthetic key stores as EMPTY, which reads as "not known" — and if
 *    TikTok ever does start supplying `user_id`, those rows become useful
 *    without anything here changing.
 *
 * Chunked, append-only, newest chunk wins — iSyn2_OrderStatus's shape, for
 * iSyn2_OrderStatus's reasons.
 */

/**
 * On the CRM site, beside IGCOM_Orders, B2B_Orders and EmbroideryRequests.
 * A plain CRM-style name, not an `iSyn2_` one: the Manufacturing site's
 * Migration Valve does not provision it and must not be asked to.
 */
export const TIKTOK_ORDERS_LIST = "TIKTOK_Orders";

/**
 * How many orders ride in one row.
 *
 * A packed TikTok order is SMALLER than a packed Shopify one — no buyer name,
 * no second status, no order number distinct from the id — so the same 120 sits
 * further inside the column's limit than IG COM's does. Kept equal anyway,
 * because a number that matches its neighbour is one fewer thing to wonder
 * about, and the headroom is free.
 */
export const ORDERS_PER_ROW = 120;

/** An order's stored facts. No buyer name, no contact detail — see the header. */
export interface ITiktokStoredOrder {
  /** TikTok's order id, which is also its only human reference. */
  id: string;
  /** ISO timestamp the order was placed. */
  createdAt: string;
  /** TikTok's own state: UNPAID, AWAITING_SHIPMENT, DELIVERED, CANCELLED… */
  status: string;
  total: number;
  currency: string;
  /** TikTok's `user_id` where it supplied one, else EMPTY — never `order:<id>`. */
  buyerKey: string;
  city: string;
  province: string;
  /** Units on the order. TikTok's line_items are one entry PER UNIT. */
  quantity: number;
  /** The SKUs on the order, deduped — what a trend is actually about. */
  skus: string[];
}

/**
 * What a caller hands in: the live row, buyer name and contacts and all. This
 * module drops them, so no caller has to remember to.
 */
export interface ITiktokSyncInput {
  id: string;
  createdAt: string;
  status: string;
  total: number;
  currency: string;
  customerKey: string;
  city: string;
  province: string;
  lineItems: readonly { sku: string; title: string }[];
}

export interface ITiktokStore {
  orders: ITiktokStoredOrder[];
  /** When the newest chunk was written, ISO, or '' where nothing is stored. */
  syncedAt: string;
  syncedBy: string;
  /** True when the list is not provisioned yet — a state the screen offers to
   *  fix, not an error. */
  missing: boolean;
}

/** One order inside a chunk. Short keys: written thousands of times. */
interface IPacked {
  i: string; d: string; s?: string; t: number; c?: string;
  k?: string; y?: string; p?: string; q: number; u?: string[];
}

const DEFAULT_CURRENCY = "PHP";

/**
 * A stored buyer key, or nothing.
 *
 * `normalizeTiktokOrder`'s `order:<id>` fallback is a live-screen convenience
 * and a stored-data lie — see the header. It is the ONE place that distinction
 * is made, so there is one place to check it is still being made.
 */
export function storedBuyerKey(customerKey: string): string {
  const k = (customerKey || "").trim();
  if (!k || k.startsWith("order:")) return "";
  return k;
}

/** Drop the buyer, flatten the lines. The one place a live row becomes a
 *  stored row, so there is one place to check that it is safe. */
export function toStoredOrder(o: ITiktokSyncInput): ITiktokStoredOrder {
  const lines = o.lineItems ?? [];
  const skus = [...new Set(lines.map((l) => (l.sku || l.title || "").trim()).filter(Boolean))];
  return {
    id: o.id,
    createdAt: o.createdAt,
    status: o.status || "",
    total: Number(o.total) || 0,
    currency: o.currency || DEFAULT_CURRENCY,
    buyerKey: storedBuyerKey(o.customerKey),
    city: o.city || "",
    province: o.province || "",
    // One entry PER UNIT, so the count IS the length. Summing a `quantity`
    // field would be right on Shopify and treble every figure here.
    quantity: lines.length,
    skus,
  };
}

/** Everything a sync could have changed, as one string. */
function fingerprint(o: ITiktokStoredOrder): string {
  return [
    o.createdAt, o.status, o.total, o.currency, o.buyerKey,
    o.city, o.province, o.quantity, o.skus.join("|"),
  ].join("\u0001");
}

/**
 * Which of `live` the list does not already agree with.
 *
 * TikTok makes this earn its keep in a way Shopify did not: an order's status
 * walks from UNPAID to DELIVERED over days, so a window re-synced tomorrow
 * genuinely HAS changed and must write — while the months behind it have not
 * and must not.
 */
export function changedOrders(
  live: readonly ITiktokStoredOrder[],
  stored: readonly ITiktokStoredOrder[],
): ITiktokStoredOrder[] {
  const have = new Map(stored.map((o) => [o.id, fingerprint(o)]));
  return live.filter((o) => have.get(o.id) !== fingerprint(o));
}

/**
 * Where a backfill should resume: unix SECONDS, or null when there is nothing
 * to resume from.
 *
 * ── THE STORE IS THE CURSOR ───────────────────────────────────────────────
 * The obvious design was a saved cursor — a row in the settings list holding
 * "the walk reached March 2026". This is better, and the reason is that a
 * saved cursor is a SECOND source of truth about the same fact. It can be
 * written when the write that earned it failed; it can survive a list the
 * user emptied; it can be stale in a way nothing on the screen would reveal.
 * Then the backfill resumes from a place that no longer matches the data and
 * leaves a hole in the history that nobody notices for a year.
 *
 * The oldest order actually IN the store cannot drift, because it is not a
 * claim about the store — it is the store. Delete the list and the backfill
 * starts over; interrupt a write and the next press resumes from whatever
 * genuinely landed. There is no state to repair because there is no state.
 *
 * ── PLUS ONE SECOND, AND IT MATTERS ───────────────────────────────────────
 * `create_time_lt` is STRICTLY less than, so passing the oldest order's own
 * timestamp would exclude every order sharing that second — a TikTok shop
 * takes several orders a second during a live, so that is a real hole rather
 * than a theoretical one. Asking for one second later re-includes the order
 * we already hold, which costs nothing: `changedOrders` recognises it and
 * writes nothing.
 */
export interface IBackfillPoint {
  /** Unix SECONDS, for `create_time_lt`. */
  cursor: number;
  /** ISO of the stored order the walk continues from. */
  from: string;
  /** Why here: a HOLE inside the stored history, or past its far END. */
  reason: "gap" | "oldest";
  /** How many days the hole spans. Zero when the reason is "oldest". */
  gapDays: number;
}

/** A hole is never smaller than this, however sparse the shop. */
const MIN_GAP_MS = 7 * 86_400_000;
/**
 * …and it must also dwarf the shop's normal spacing. A shop selling hourly
 * and a shop selling monthly both have "quiet stretches"; only the ratio
 * tells them apart, so the floor and the ratio both have to be cleared.
 */
const GAP_RATIO = 20;

export function backfillCursor(
  stored: readonly ITiktokStoredOrder[],
  /**
   * Skip gap-filling and go straight past the far end.
   *
   * The caller sets this once a gap press has proved the hole was not one —
   * a genuinely quiet stretch would otherwise be re-fetched for ever and the
   * button would never reach the start of the history.
   */
  opts: { ignoreGaps?: boolean } = {},
): IBackfillPoint | null {
  // An unparseable date cannot position anything — skipped rather than
  // allowed to collapse the cursor to NaN and break every future press.
  const times = stored
    .map((o) => Date.parse(o.createdAt))
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => b - a);
  if (times.length === 0) return null;

  const at = (ms: number, reason: "gap" | "oldest", gapDays = 0): IBackfillPoint => ({
    cursor: Math.floor(ms / 1000) + 1,
    from: new Date(ms).toISOString(),
    reason,
    gapDays,
  });

  if (opts.ignoreGaps || times.length < 3) return at(times[times.length - 1], "oldest");

  // Every step down the timeline, and the middle one — the median is used
  // rather than the mean because one multi-year hole would drag a mean up
  // past every real gap and hide the thing we are looking for.
  const steps: number[] = [];
  for (let i = 0; i < times.length - 1; i++) steps.push(times[i] - times[i + 1]);
  const median = [...steps].sort((a, b) => a - b)[Math.floor(steps.length / 2)] || 0;

  let worst = -1;
  let worstAt = -1;
  for (let i = 0; i < steps.length; i++) {
    if (steps[i] > worst) { worst = steps[i]; worstAt = i; }
  }

  // A HOLE IN THE MIDDLE BEATS THE FAR END, and this is the whole reason the
  // function is not a one-line `Math.min`. The store is NOT guaranteed to be
  // one contiguous run backwards from today: this shop's list already holds a
  // block from June 2022, left by the syncs that ran while the walk was
  // sorted ascending, with years of nothing between it and the recent orders.
  // Taking the single oldest row would ask TikTok for orders before June
  // 2022, find none, and report the history COMPLETE with that hole still in
  // it — a silent wrong answer of exactly the kind a stored cursor was
  // rejected for producing. So the walk fills the hole first.
  if (worstAt >= 0 && worst >= MIN_GAP_MS && worst >= median * GAP_RATIO) {
    return at(times[worstAt], "gap", Math.round(worst / 86_400_000));
  }
  return at(times[times.length - 1], "oldest");
}

export function pack(o: ITiktokStoredOrder): IPacked {
  return {
    i: o.id, d: o.createdAt, t: o.total, q: o.quantity,
    ...(o.status ? { s: o.status } : {}),
    ...(o.currency && o.currency !== DEFAULT_CURRENCY ? { c: o.currency } : {}),
    ...(o.buyerKey ? { k: o.buyerKey } : {}),
    ...(o.city ? { y: o.city } : {}),
    ...(o.province ? { p: o.province } : {}),
    ...(o.skus.length ? { u: o.skus } : {}),
  };
}

/**
 * A chunk's entries.
 *
 * A row whose JSON will not parse contributes NOTHING rather than throwing:
 * one malformed row from a hand-edit costs its own orders, not every other
 * chunk on the list.
 */
export function unpackChunk(raw: unknown): ITiktokStoredOrder[] {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const v: unknown = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    return v.flatMap((e): ITiktokStoredOrder[] => {
      const p = e as Partial<IPacked>;
      if (!p.i) return [];
      return [{
        id: String(p.i),
        createdAt: String(p.d ?? ""),
        status: String(p.s ?? ""),
        total: Number(p.t) || 0,
        currency: String(p.c ?? DEFAULT_CURRENCY),
        buyerKey: String(p.k ?? ""),
        city: String(p.y ?? ""),
        province: String(p.p ?? ""),
        quantity: Number(p.q) || 0,
        skus: Array.isArray(p.u) ? p.u.map(String) : [],
      }];
    });
  } catch {
    return [];
  }
}
